/**
 * The guild somebody is in: who else is in it, what it says, and who may do what.
 *
 * Everything here is a request. What the panel shows is whatever the world last
 * sent back, so a promotion the world refused is a promotion that does not
 * appear, rather than one that appears and then vanishes.
 */

import { useState } from 'react';
import { GuildAct, type GuildActValue, type GuildWire } from '@inverse/protocol';

export function Guild({
  guild,
  me,
  onAct,
}: {
  guild: GuildWire;
  me: string;
  onAct: (act: GuildActValue, text?: string) => void;
}): JSX.Element {
  const [typed, setTyped] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  if (guild.asked) {
    return (
      <div className="guild">
        <p className="hint">
          {guild.asked.by} has asked you into {guild.asked.name}.
        </p>
        <div className="row">
          <button type="button" className="primary" onClick={() => onAct(GuildAct.Accept)}>
            Join
          </button>
          <button type="button" onClick={() => onAct(GuildAct.Decline)}>
            No thanks
          </button>
        </div>
      </div>
    );
  }

  if (!guild.name) {
    return (
      <div className="guild">
        <p className="hint">
          You are in no guild. Start one, or wait to be asked into somebody else's.
        </p>
        <form
          className="friends-add"
          onSubmit={(event) => {
            event.preventDefault();
            if (typed.trim().length >= 3) onAct(GuildAct.Found, typed.trim());
            setTyped('');
          }}
        >
          <input
            type="text"
            maxLength={24}
            value={typed}
            placeholder="Name a guild"
            onChange={(event) => setTyped(event.target.value)}
          />
          <button type="submit">Found it</button>
        </form>
      </div>
    );
  }

  const officer = guild.rank === 'owner' || guild.rank === 'officer';
  const here = guild.members.filter((one) => one.online).length;

  return (
    <div className="guild">
      <div className="guild-head">
        <strong>{guild.name}</strong>
        <span className="quiet">
          {here} of {guild.members.length} here
        </span>
      </div>

      {notice === null ? (
        <p className="hint guild-notice">
          {guild.motd || 'Nothing written on the notice.'}
          {officer ? (
            <button type="button" onClick={() => setNotice(guild.motd)}>
              Write it
            </button>
          ) : null}
        </p>
      ) : (
        <form
          className="friends-add"
          onSubmit={(event) => {
            event.preventDefault();
            onAct(GuildAct.Notice, notice);
            setNotice(null);
          }}
        >
          <input
            type="text"
            maxLength={200}
            value={notice}
            placeholder="What the guild is doing"
            onChange={(event) => setNotice(event.target.value)}
          />
          <button type="submit">Write</button>
        </form>
      )}

      <div className="friends-list">
        {guild.members.map((one) => (
          <div className={one.online ? 'friend on' : 'friend'} key={one.name}>
            <span className="friend-dot" aria-hidden />
            <span className="friend-name">{one.name}</span>
            <span className="quiet friend-where">{one.rank}</span>

            {guild.rank === 'owner' && one.name !== me ? (
              <>
                <button
                  type="button"
                  title={
                    one.rank === 'officer'
                      ? `Make ${one.name} a member`
                      : `Make ${one.name} an officer`
                  }
                  onClick={() =>
                    onAct(
                      one.rank === 'officer' ? GuildAct.MakeMember : GuildAct.MakeOfficer,
                      one.name,
                    )
                  }
                >
                  {one.rank === 'officer' ? 'Demote' : 'Promote'}
                </button>
                <button
                  type="button"
                  title={`Put ${one.name} out of the guild`}
                  onClick={() => onAct(GuildAct.Remove, one.name)}
                >
                  Remove
                </button>
              </>
            ) : null}
          </div>
        ))}
      </div>

      {officer ? (
        <form
          className="friends-add"
          onSubmit={(event) => {
            event.preventDefault();
            if (typed.trim()) onAct(GuildAct.Ask, typed.trim());
            setTyped('');
          }}
        >
          <input
            type="text"
            maxLength={20}
            value={typed}
            placeholder="Ask somebody in"
            onChange={(event) => setTyped(event.target.value)}
          />
          <button type="submit">Ask</button>
        </form>
      ) : null}

      <button type="button" className="wide danger" onClick={() => onAct(GuildAct.Leave)}>
        {guild.rank === 'owner' ? 'Close the guild' : 'Leave the guild'}
      </button>
    </div>
  );
}
